<template>
    <div>
        <div
            v-if="!isLoading"
            class="page-items-content"
        >
            <div class="flex flex-col gap-7">
                <div
                    v-for="(item) in enrichedPageItems"
                    :key="item.id"
                    class="page-item bg-white w-full rounded-md px-6 py-5"
                >
                    <h5 class="mb-4">
                        {{ item.title }}
                    </h5>
                    <div class="flex flex-col lg:flex-row gap-5 ">
                        <img
                            v-if="item.image"
                            class="page-item-image w-full lg:w-2/5 self-start cursor-pointer"
                            :alt="item.title"
                            :src="item.image"
                            @click="openModal(item)"
                        >
                        <div>
                            <p
                                v-if="item.publishedAt"
                                class="text-neutral-500 mb-2 date-text"
                            >
                                {{ $t('timeline.publish_at', { date: formatDate(item.publishedAt, $t('timeline.date_format')) }) }}
                            </p>
                            <custom-page-description :description="item.description" />
                            <div
                                v-if="item.url"
                                class="flex mt-4"
                            >
                                <NuxtLink
                                    :href="item.url"
                                    target="_blank"
                                    rel="noopener"
                                    class="btn btn-primary flex items-center"
                                >
                                    <span class="text-sm">{{ $t('timeline.more_info') }}</span>
                                    <icon-external-link
                                        class="icon-micro ml-2"
                                        :custom-color="buttonTextColor"
                                    />
                                </NuxtLink>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type PropType, ref, type Ref, watch } from 'vue';
    import type { IPageItem } from '~/types/types';
    import { useAccountStore } from '~/stores/accountStore';
    import IconExternalLink from '~/components/icons/IconExternalLink.vue';
    import { formatDate, generateUuid } from '~/utils/helpers';
    import CustomPageDescription from '~/components/common/CustomPageDescription.vue';
    import { useModalStore } from '~/stores/modalStore';

    const props = defineProps({
        isLoading: {
            type: Boolean,
            required: false,
            default: true,
        },
        pageItems: {
            type: Array as PropType<IPageItem[]>,
            required: false,
            default: () => [],
        }
    });

    const { buttonColor, buttonTextColor } = useAccountStore().state;
    const enrichedPageItems: Ref<IPageItem[]> = ref([]);
    const modalStore = useModalStore();

    watch(
        () => props.pageItems,
        pageItems => {
            enrichedPageItems.value = pageItems.map((item: IPageItem) => {
                return {
                    ...item,
                    id: item.id || generateUuid(),
                };
            });
        }
    );

    const openModal = (item: IPageItem) => {
        modalStore.setModalData(
            item.title,
            [item.image],
            true
        );
    };
</script>

<style scoped lang="scss">
    $buttonColor: v-bind(buttonColor);
    $buttonTextColor: v-bind(buttonTextColor);

    .page-item {
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.07);
        transition: all 0.15s ease;

        &:hover {
            box-shadow: 0 1px 20px 0 rgba(0,0,0,.1);
        }
    }

    .page-item-image {
        object-fit: contain;
    }

    .icon-micro,
    .btn-primary span {
        transition: all 0.15s ease;
        font-size: 0.85rem;
    }

    .btn-primary {
        border-color: $buttonColor;
        background-color: $buttonColor;
        color: $buttonTextColor;
        padding: 0.4rem 1.5rem;

        &:hover {
            opacity: 0.8;

            span {
                transform: translateX(-1px);
            }

            svg {
                transform: translateX(3px);
            }
        }
    }

    .date-text {
        font-size: 0.825rem;
    }
</style>
